/* global fetch */

import querystring from 'querystring';
import { config } from '../config';
import { headers, APIResponse, stleaf, fetchAPI, SpanTag } from '../helpers';
import { MeasurementSystem } from '@birdi/types/measurement';

export const getByMissionId = (
  st: SpanTag,
  missionId: string,
): Promise<APIResponse> => {
  const queryString = querystring.stringify({ 'mission-id': missionId });
  const url = new URL(
    `${config.API_URL}/v2/annotations/geojson?${queryString}`,
  );
  return fetchAPI(stleaf(st, 'vei5im'), url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

interface InsertOpts {
  missionId: string;
  geojson: any; // parsable geojson
}

interface InsertV2Opts {
  missionId: string;
  geojson: any; // parsable geojson
}

export const insertV2 = (
  st: SpanTag,
  fields: InsertV2Opts,
): Promise<APIResponse> => {
  const url = new URL(`${config.API_URL}/v2/annotations`);
  return fetchAPI(stleaf(st, 'qzol2e'), url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(fields),
  });
};

/**
 *
 * @deprecated
 * TODO: Replace with updateShapePartial
 */
export const updateById = (
  st: SpanTag,
  id: string,
  geojson,
): Promise<APIResponse> => {
  // Only send useful properties
  const properties = {
    bd_color: geojson.properties.bd_color,
    bd_label: geojson.properties.bd_label,
    bd_skey: geojson.properties.bd_skey,
  };
  const body = Object.assign({}, geojson, { properties });
  const url = new URL(`${config.API_URL}/v2/annotations/${id}`);
  return fetchAPI(stleaf(st, 'leis6e'), url.toString(), {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(body),
  });
};

export const deleteById = (st: SpanTag, id: string): Promise<APIResponse> => {
  const url = new URL(`${config.API_URL}/v2/annotations/${id}`);
  return fetchAPI(stleaf(st, 'phee8o'), url.toString(), {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

export const importShapefile = (
  st: SpanTag,
  missionId: string,
  blob,
): Promise<APIResponse> => {
  const url = new URL(
    `${config.API_URL}/v2/annotations/shapefile?mission-id=${missionId}`,
  );
  return fetchAPI(stleaf(st, 'ij7lao'), url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/octet-stream',
      Accept: 'application/json',
    },
    body: blob,
  });
};

export const importGeojson = (
  st: SpanTag,
  missionId: string,
  blob,
): Promise<APIResponse> => {
  const url = new URL(
    `${config.API_URL}/v2/annotations/file/geojson?mission-id=${missionId}`,
  );
  return fetchAPI(stleaf(st, 'doz7th'), url.toString(), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/octet-stream',
      Accept: 'application/json',
    },
    body: blob,
  });
};

export const importGeojsonV2 = (
  st: SpanTag,
  fields: InsertOpts,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/annotations/geojson`;
  return fetchAPI(stleaf(st, 'hode8h'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(fields),
  });
};

// TODO: Extend this to update any property
export const updateShapePartial = (
  st: SpanTag,
  id: string,
  sortKey: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/annotations/${id}/attributes`;
  return fetchAPI(stleaf(st, 'teish6'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ sortKey }),
  });
};

export type IncludedAnnotation = 'Polygon' | 'LineString' | 'Point';

export interface AnnotationReport {
  id: string;
  name: string;
  missionId: string;
  s3Key?: string;
  includedAnnotations: IncludedAnnotation[];
  status: 'pending' | 'complete' | 'failed';
  createdAt: string;
}

export interface GenerateAnnotationReportParams {
  name: string;
  mediaId: string;
  measurementSystem: MeasurementSystem;
  includedAnnotations: IncludedAnnotation[];
}

export const generateReport = (
  st: SpanTag,
  missionId: string,
  params: GenerateAnnotationReportParams,
): Promise<APIResponse<{ id: string }>> => {
  const url = `${config.API_URL}/v2/annotations/reports?mission-id=${missionId}`;
  return fetchAPI(stleaf(st, 'quey7e'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export const listReports = (
  st: SpanTag,
  missionId: string,
): Promise<APIResponse<AnnotationReport[]>> => {
  const url = `${config.API_URL}/v2/annotations/reports?mission-id=${missionId}`;
  return fetchAPI(stleaf(st, 'esa0la'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};
export const getReportURL = (
  st: SpanTag,
  missionId: string,
  reportId: string,
): Promise<APIResponse<string>> => {
  const url = `${config.API_URL}/v2/annotations/reports/${reportId}/download?mission-id=${missionId}`;
  return fetchAPI(stleaf(st, 'sahch5'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const deleteReport = (
  st: SpanTag,
  missionId: string,
  reportId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/annotations/reports/${reportId}?mission-id=${missionId}`;
  return fetchAPI(stleaf(st, 'aivei8'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};
